import React, { useMemo } from 'react';
import { Box, Typography, Chip } from '@mui/material';
import { DATE_FORMAT_SLASH_WITH_SHORT_YEAR, fDate } from '../../../../../utils/formatTime';
import CrossIconSvgV2 from '../../../../../assets/svg-fc/CrossIconSvgV2';
import { getFullName } from '../../../../../utils/commonUtils';
import { useAppSelector } from '../../../../../hooks/storeHooks';
import './filterChips.scss';

export interface AppliedFilters {
  vendors?: string[];
  assignees?: string[];
  orderId?: string;
  eventDateStart?: string;
  eventDateEnd?: string;
  createdDateStart?: string;
  createdDateEnd?: string;
  vendorEventStatus?: string[];
  eventType?: string;
  numberOfEvents?: number;
}

interface FilterChipsProps {
  filters: AppliedFilters;
  numberOfEvents: number;
  hasFilters?: string | boolean;
  onRemoveFilter: (filterKey: string, filterId?: string) => void;
  onClearAll: () => void;
}

const FilterChips: React.FC<FilterChipsProps> = ({ filters, onRemoveFilter, onClearAll, numberOfEvents, hasFilters }) => {

  if (!hasFilters) {
    return null;
  }

  const formatDate = (dateString: string) => {
    try {
      return fDate(dateString, DATE_FORMAT_SLASH_WITH_SHORT_YEAR);
    } catch {
      return dateString;
    }
  };

  const createdDateRange = () => {
    if (filters.createdDateStart && filters.createdDateEnd) {
      if (filters.createdDateStart === filters.createdDateEnd) {
        return `${formatDate(filters.createdDateStart || '')}`;
      }
      return `${formatDate(filters.createdDateStart)} - ${formatDate(filters.createdDateEnd)}`;
    }
    return '';
  };

  const eventDateRange = () => {
    if (filters.eventDateStart && filters.eventDateEnd) {
      if (filters.eventDateStart === filters.eventDateEnd) {
        return `${formatDate(filters.eventDateStart || '')}`;
      }
      return `${formatDate(filters.eventDateStart)} - ${formatDate(filters.eventDateEnd)}`;
    }
    return '';
  };

  return (
    <Box className={`filterChips ${numberOfEvents === 0 ? 'noResults' : ''}`}>
      <Box className="filterChipsList">
        {filters.orderId && (
          <Chip
            label={`Order ID: ${filters.orderId}`}
            onDelete={() => onRemoveFilter('orderId')}
            deleteIcon={<CrossIconSvgV2 />}
            className="filterChip"
          />
        )}
        {filters.eventDateStart && filters.eventDateEnd && (
          <Chip
            label={`Event date: ${eventDateRange()}`}
            onDelete={() => onRemoveFilter('eventDate')}
            deleteIcon={<CrossIconSvgV2 />}
            className="filterChip"
          />
        )}
        {filters.createdDateStart && filters.createdDateEnd && (
          <Chip
            label={`Created date: ${createdDateRange()}`}
            onDelete={() => onRemoveFilter('createdDate')}
            deleteIcon={<CrossIconSvgV2 />}
            className="filterChip"
          />
        )}
        {filters.vendorEventStatus?.map((status) => (
          <Chip
            key={`status-${status}`}
            label={`Event status: ${status}`}
            onDelete={() => onRemoveFilter('vendorEventStatus', status)}
            deleteIcon={<CrossIconSvgV2 />}
            className="filterChip"
          />
        ))}
        {filters.eventType && (
          <Chip
            label={`Event type: ${filters.eventType}`}
            onDelete={() => onRemoveFilter('eventType')}
            deleteIcon={<CrossIconSvgV2 />}
            className="filterChip"
          />
        )}
        <Typography component="button" type="button" className="filterChipsClearAll" onClick={onClearAll}>
          Clear all
        </Typography>
      </Box>
    </Box>
  );
};

export default FilterChips;
